/**
 * Portal catalogue browse — Step 71, relaid to the Global2 capture's anatomy
 * (Kai: "favourites to the top, and things sorted by groups / folders"): a
 * left rail of category folders under Favourites / Popular items / All
 * service items, a card grid, and a star on every card. Favourites are the
 * SAME per-user pins the agent surface reads. Only PUBLISHED items ever
 * reach this response.
 *
 * Draft-first on purpose: the only free text here is the service SEARCH box,
 * and the only mutation is starring a favourite — a one-click toggle, not a
 * draft. The request FORM (which is a draft, and is guarded) lives on the
 * item page.
 */
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Flame, Search, Star } from 'lucide-react';
import { portalApi } from '../../features/portal/api';
import { CatalogueIcon } from '../../features/fields/catalogueIcons';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import { qk } from '../../lib/queryClient';
import { useUrlState } from '../../lib/urlState';
import { cn } from '../../lib/utils';

export function PortalCataloguePage() {
  const queryClient = useQueryClient();
  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: qk.portalCatalogue,
    queryFn: portalApi.catalogue,
  });
  // Popular is the DEFAULT shelf (Kai) — falling back to All when nothing
  // is hand-picked yet, so a fresh org never lands on an empty page.
  const [railRaw, setRail] = useUrlState('cat', 'popular');
  const [search, setSearch] = useUrlState('q', '');

  const favourites = useMemo(() => new Set(data?.favourites ?? []), [data]);
  const star = useMutation({
    mutationFn: ({ id, on }: { id: string; on: boolean }) => portalApi.setFavourite(id, on),
    onSettled: () => queryClient.invalidateQueries({ queryKey: qk.portalCatalogue }),
  });

  const rail = railRaw === 'popular' && (data?.popular ?? []).length === 0 ? 'all' : railRaw;
  const q = search.trim().toLowerCase();
  const matches = useMemo(
    () =>
      (data?.items ?? []).filter(
        (it) => !q || it.name.toLowerCase().includes(q) || (it.description ?? '').toLowerCase().includes(q),
      ),
    [data, q],
  );
  const roots = (data?.categories ?? []).filter((c) => !c.parentId);
  const childIds = (rootId: string) => {
    const ids = new Set([rootId]);
    let grew = true;
    while (grew) {
      grew = false;
      for (const c of data?.categories ?? []) {
        if (c.parentId && ids.has(c.parentId) && !ids.has(c.id)) {
          ids.add(c.id);
          grew = true;
        }
      }
    }
    return ids;
  };
  const shown =
    rail === 'favourites'
      ? matches.filter((it) => favourites.has(it.id))
      : rail === 'popular'
        ? (data?.popular ?? [])
            .map((id) => matches.find((it) => it.id === id))
            .filter((it): it is NonNullable<typeof it> => !!it)
        : rail === 'all'
          ? matches
          : matches.filter((it) => (it.categoryIds ?? []).some((cid) => childIds(rail).has(cid)));

  const railEntries: { key: string; label: string; icon?: typeof Star }[] = [
    ...(favourites.size > 0 ? [{ key: 'favourites', label: 'Favourites', icon: Star }] : []),
    ...((data?.popular ?? []).length > 0 ? [{ key: 'popular', label: 'Popular items', icon: Flame }] : []),
    { key: 'all', label: 'All service items' },
    ...roots.map((c) => ({ key: c.id, label: c.name })),
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <h1 className="text-lg font-semibold">Service catalogue</h1>
          <p className="text-xs text-muted-foreground">Browse the services offered and raise a request</p>
        </div>
        <div className="relative ml-auto">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search services"
            className="h-9 w-full pl-8 sm:w-64"
          />
        </div>
      </div>

      {isError && (
        <div className="space-y-2 rounded-md border border-destructive/30 bg-destructive/10 p-3">
          <p className="text-sm font-medium text-destructive">Could not load the catalogue</p>
          <p className="text-xs text-destructive/80">{(error as Error).message}</p>
          <button
            onClick={() => refetch()}
            className="text-xs text-destructive/80 underline hover:text-destructive"
          >
            Retry
          </button>
        </div>
      )}
      {isPending && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }, (_, i) => (
            <Skeleton key={i} className="h-28 w-full" />
          ))}
        </div>
      )}

      {data && !isPending && (
        <div className="flex flex-col gap-4 md:flex-row md:gap-6">
          {/* Step 141 follow-up — the category rail is a scrolling chip row on a
              phone and a vertical rail from `md:`. Side by side, the rail took
              208px of a 390px screen and the item grid was crushed into what
              was left. */}
          <nav className="-mx-1 flex gap-1 overflow-x-auto px-1 pb-1 md:mx-0 md:block md:w-52 md:shrink-0 md:space-y-0.5 md:overflow-visible md:px-0 md:pb-0">
            {railEntries.map((r) => (
              <button
                key={r.key}
                onClick={() => setRail(r.key)}
                className={cn(
                  'flex shrink-0 items-center gap-2 whitespace-nowrap rounded-md px-2.5 py-1.5 text-left text-sm transition-colors md:w-full md:whitespace-normal',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  rail === r.key
                    ? 'bg-accent font-medium text-accent-foreground'
                    : 'text-muted-foreground hover:bg-accent/60 hover:text-foreground',
                )}
              >
                {r.icon && <r.icon className="h-4 w-4" />}
                <span className="truncate">{r.label}</span>
              </button>
            ))}
          </nav>
          <div className="min-w-0 flex-1">
            {rail === 'popular' && (
              <p className="mb-2 text-xs text-muted-foreground">Hand-picked by the support team</p>
            )}
            {shown.length === 0 ? (
              <div className="rounded-lg border border-dashed p-10 text-center text-sm text-muted-foreground">
                {q ? 'Nothing matches that search.' : 'Nothing here yet.'}
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {shown.map((it) => (
                  <div
                    key={it.id}
                    className="group relative rounded-lg border bg-card transition-colors hover:border-brand"
                  >
                    <Link
                      to={`/portal/catalogue/${it.id}`}
                      className="block p-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <span className="mb-2 flex h-9 w-9 items-center justify-center rounded-md bg-brand/10 text-brand">
                        <CatalogueIcon
                          name={it.icon}
                          attachmentId={it.iconAttachmentId}
                          className="h-5 w-5"
                        />
                      </span>
                      <p className="pr-6 text-sm font-semibold group-hover:text-brand">{it.name}</p>
                      {it.description && (
                        <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{it.description}</p>
                      )}
                    </Link>
                    <button
                      aria-label={favourites.has(it.id) ? 'Remove from favourites' : 'Add to favourites'}
                      onClick={() => star.mutate({ id: it.id, on: !favourites.has(it.id) })}
                      className={cn(
                        'absolute right-2.5 top-2.5 rounded p-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                        favourites.has(it.id)
                          ? 'text-amber-500'
                          : 'text-muted-foreground/40 opacity-0 transition-opacity hover:text-muted-foreground group-hover:opacity-100 focus-visible:opacity-100',
                      )}
                    >
                      <Star className={cn('h-4 w-4', favourites.has(it.id) && 'fill-current')} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
